import axios, { AxiosError, AxiosResponse } from 'axios';
import { all, call, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { SubscriptionError } from '@app/types/types';
import { notifyError, notifySuccess, toastMessage } from '@helpers/index';
import { fetchCustomerRequest } from '@modules/customer/core/actions';
import { fetchOrderScheduleRequest } from '@modules/order-schedule/core/actions';
import { fetchOrderHistoryRequest } from '@modules/orders/core/actions';
import type { ProductUpdatedData } from '@modules/product-updated/core/types';
import { fetchProductRequest } from '@modules/products/core/actions';
import { updateSettings } from '@modules/settings/core/actions';
import { translateOutsideReact } from '@shared/i18n/intlInstance';

import { getDisabledRecipes } from '../utils/getDisabledRecipes';
import {
  calcScore,
  getDisabledRecipesFromAllergies,
  transformRecommendationToProduct,
} from '../utils/recalculateProductHelpers';
import {
  addDiscountFailure,
  addDiscountRequest,
  addDiscountSuccess,
  applyBenefitFailure,
  applyBenefitRequest,
  applyBenefitSuccess,
  applyCancellationOfferFailure,
  applyCancellationOfferRequest,
  applyCancellationOfferSuccess,
  cancelSubscriptionFailure,
  cancelSubscriptionRequest,
  cancelSubscriptionSuccess,
  changePaymentMethodFailure,
  changePaymentMethodRequest,
  changePaymentMethodSuccess,
  fetchAllergiesFailure,
  fetchAllergiesSuccess,
  fetchBenefitContentFailure,
  fetchBenefitContentRequest,
  fetchBenefitContentSuccess,
  fetchCancellationFlowFailure,
  fetchCancellationFlowRequest,
  fetchCancellationFlowSuccess,
  fetchCancellationOfferFailure,
  fetchCancellationOfferRequest,
  fetchCancellationOfferSuccess,
  fetchFlowRewardBannerFailure,
  fetchFlowRewardBannerRequest,
  fetchFlowRewardBannerSuccess,
  fetchHealthIssuesFailure,
  fetchHealthIssuesSuccess,
  fetchSubscriptionsFailure,
  fetchSubscriptionsRequest,
  fetchSubscriptionsSuccess,
  pauseSubscriptionFailure,
  pauseSubscriptionRequest,
  pauseSubscriptionSuccess,
  placeOrderFailure,
  placeOrderRequest,
  placeOrderSuccess,
  reactivateSubscriptionFailure,
  reactivateSubscriptionRequest,
  reactivateSubscriptionSuccess,
  recalculateProductFailure,
  recalculateProductRequest,
  recalculateProductReset,
  recalculateProductSuccess,
  removeDiscountFailure,
  removeDiscountRequest,
  removeDiscountSuccess,
  requestCallbackFailure,
  requestCallbackRequest,
  requestCallbackSuccess,
  rescheduleOrderFailure,
  rescheduleOrderRequest,
  rescheduleOrderSuccess,
  resumeSubscriptionFailure,
  resumeSubscriptionRequest,
  resumeSubscriptionSuccess,
  skipNextOrderFailure,
  skipNextOrderRequest,
  skipNextOrderSuccess,
  swapLineFailure,
  swapLineRequest,
  swapLineSuccess,
  updatePetAndSwapLineFailure,
  updatePetAndSwapLineRequest,
  updatePetAndSwapLineSuccess,
  updatePetDetailsFailure,
  updatePetDetailsRequest,
  updatePetDetailsSuccess,
  updateSubscriptionAddressFailure,
  updateSubscriptionAddressRequest,
  updateSubscriptionAddressSuccess,
  updateSubscriptionNextChargeDateFailure,
  updateSubscriptionNextChargeDateRequest,
  updateSubscriptionNextChargeDateSuccess,
} from './actions';
import {
  ADD_DISCOUNT_REQUEST,
  APPLY_BENEFIT_REQUEST,
  APPLY_CANCELLATION_OFFER_REQUEST,
  CANCEL_SUBSCRIPTION_REQUEST,
  CHANGE_PAYMENT_METHOD_REQUEST,
  FETCH_ALLERGIES_REQUEST,
  FETCH_BENEFIT_CONTENT_REQUEST,
  FETCH_CANCELLATION_FLOW_REQUEST,
  FETCH_CANCELLATION_OFFER_REQUEST,
  FETCH_FLOW_REWARD_BANNER_REQUEST,
  FETCH_HEALTH_ISSUES_REQUEST,
  FETCH_SUBSCRIPTIONS_REQUEST,
  FETCH_SUBSCRIPTIONS_SUCCESS,
  PAUSE_SUBSCRIPTION_REQUEST,
  PLACE_ORDER_REQUEST,
  REACTIVATE_SUBSCRIPTION_REQUEST,
  RECALCULATE_PRODUCT_REQUEST,
  REMOVE_DISCOUNT_REQUEST,
  REQUEST_CALLBACK_REQUEST,
  RESCHEDULE_ORDER_REQUEST,
  RESUME_SUBSCRIPTION_REQUEST,
  SKIP_NEXT_ORDER_REQUEST,
  SWAP_LINE_REQUEST,
  UPDATE_PET_AND_SWAP_LINE_REQUEST,
  UPDATE_PET_DETAILS_REQUEST,
  UPDATE_SUBSCRIPTION_ADDRESS_REQUEST,
  UPDATE_SUBSCRIPTION_NEXT_CHARGE_DATE_REQUEST,
} from './actionTypes';
import { getLineItemSelector, getPetSelector, getSubscriptionOptionsSelector } from './selectors';
import {
  ApplyBenefitParams,
  ApplyCancellationOfferParams,
  CancelSubscriptionParams,
  IPet,
  PauseSubscriptionParams,
  RecalculateProductResponse,
  ReferenceOption,
  SwapLineParams,
  UpdatePetAndSwapLineParams,
} from './types';

const getSubscriptions = () => axios.get<ISubscription[]>(`/api/v1/subscriptions`);
const getAllergies = () =>
  axios.get<{ success: boolean; data: { allergies: ReferenceOption[] } }>(
    `/api/v1/configurator/allergies`,
  );
const getHealthIssues = () =>
  axios.get<{
    success: boolean;
    data: { health_issues: (ReferenceOption & { description?: string })[] };
  }>(`/api/v1/configurator/health_issues`);
const updatePetDetails = (id: number, petName: string, lineId: string) =>
  axios.put<{ success: boolean; data: IPet }>(`/api/v1/subscriptions/${id}/update_pet`, {
    subscription: { petName, lineId },
  });

function* fetchAllergiesSaga() {
  try {
    const response: AxiosResponse<{ success: boolean; data: { allergies: ReferenceOption[] } }> =
      yield call(getAllergies);
    yield put(
      fetchAllergiesSuccess({
        allergies: response.data.data.allergies,
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchAllergiesFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* fetchHealthIssuesSaga() {
  try {
    const response: AxiosResponse<{
      success: boolean;
      data: { health_issues: (ReferenceOption & { description?: string })[] };
    }> = yield call(getHealthIssues);
    yield put(
      fetchHealthIssuesSuccess({
        healthIssues: response.data.data.health_issues,
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchHealthIssuesFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

const updateSubscriptionAddress = async (id: number, params: any) => {
  const response = await axios.post<ISubscription>(
    `/api/v1/subscriptions/${id}/update_address`,
    params,
  );
  return response.data;
};

const cancelSubscription = async (id: number, params: CancelSubscriptionParams) => {
  const response = await axios.post<ISubscription>(`/api/v1/subscriptions/${id}/cancel`, params);
  return response.data;
};

const fetchCancellationFlow = async (id: number) => {
  const response = await axios.post<ISubscription>(`/api/v1/subscriptions/${id}/cancellationFlow`);
  return response.data;
};

const pauseSubscription = async (id: number, params: PauseSubscriptionParams) => {
  const response = await axios.post<ISubscription>(`/api/v1/subscriptions/${id}/pause`, params);
  return response.data;
};

const resumeSubscription = async (id: number) => {
  const response = await axios.post<ISubscription>(`/api/v1/subscriptions/${id}/resume`);
  return response.data;
};

const reactivateSubscription = async (id: number) => {
  const response = await axios.post<ISubscription>(`/api/v1/subscriptions/${id}/reactivate`);
  return response.data;
};

const updateSubscriptionNextChargeDate = async (id: number, params: any) => {
  const response = await axios.post<ISubscription>(
    `/api/v1/subscriptions/${id}/next_charge_date`,
    params,
  );
  return response.data;
};

const rescheduleOrder = async (subscriptionId: number, params: any) => {
  const response = await axios.post<any>(
    `/api/v1/subscriptions/${subscriptionId}/reschedule`,
    params,
  );
  return response.data;
};

const placeOrder = async (subscriptionId: number) => {
  const response = await axios.post<any>(`/api/v1/subscriptions/${subscriptionId}/place_order`);
  return response.data;
};

const skipNextOrder = async (subscriptionId: number) => {
  const response = await axios.post<any>(`/api/v1/subscriptions/${subscriptionId}/skip_next_order`);
  return response.data;
};

const changePaymentMethod = async (subscriptionId: number, params: any) => {
  const response = await axios.put<ISubscription>(
    `/api/v1/subscriptions/${subscriptionId}/change_payment_method`,
    params,
  );
  return response.data;
};

const addDiscount = async (id: number, params: any) => {
  const response = await axios.post<any>(`/api/v1/subscriptions/${id}/add_discount`, params);
  return response.data;
};

const removeDiscount = async (id: number, params: any) => {
  const response = await axios.delete<any>(
    `/api/v1/subscriptions/${id}/remove_discount/${params?.discountShopifyId}`,
  );
  return response.data;
};

const swapLine = async (id: number, params: SwapLineParams) => {
  const response = await axios.put<ISubscription>(`/api/v1/subscriptions/${id}/swap`, params);
  return response.data;
};

const fetchFlowRewardBanner = async (id: number) => {
  const response = await axios.get<any>(`/api/v1/subscriptions/${id}/flow_reward_banner`);
  return response.data;
};

const fetchBenefitContent = async (id: number) => {
  const response = await axios.get<ISubscription>(`/api/v1/subscriptions/${id}/cancellationFlow`);
  return response.data;
};

const requestCallback = async (id: number, params: any) => {
  const response = await axios.post<any>(`/api/v1/subscriptions/${id}/request_callback`, {
    params,
  });
  return response.data;
};

const applyBenefit = async (id: number, params: ApplyBenefitParams) => {
  const response = await axios.post<ISubscription>(
    `/api/v1/subscriptions/${id}/apply_benefit`,
    params,
  );
  return response.data;
};

const fetchCancellationOffer = async (subscriptionId: number, cancellationFlowId: number) => {
  const response = await axios.get<ISubscription>(
    `/api/v1/subscriptions/${subscriptionId}/cancellation_offer/${cancellationFlowId}`,
  );
  return response.data;
};

const applyCancellationOffer = async (id: number, params: ApplyCancellationOfferParams) => {
  const response = await axios.post<ISubscription>(
    `/api/v1/subscriptions/${id}/apply_cancellation_offer`,
    params,
  );
  return response.data;
};

const fetchRecommendation = async (scoresParam: string) => {
  const params = new URLSearchParams();
  params.append('scores', scoresParam);
  const response = await axios.get<{ success: boolean; data: any[] }>(
    `/api/v1/configurator/recommendation?${params.toString()}`,
  );
  return response.data.data;
};

const updatePetAndSwapLine = async (id: number, params: UpdatePetAndSwapLineParams) => {
  const response = await axios.put<ISubscription>(
    `/api/v1/subscriptions/${id}/update_pet_and_swap_line`,
    params,
  );
  return response;
};

function* requestCallbackSaga(action: ReturnType<typeof requestCallbackRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      requestCallback(action.payload.id, action.payload.params),
    );

    yield put(
      requestCallbackSuccess({
        data: response.data,
        id: action.payload.id,
      }),
    );
  } catch (e: unknown) {
    yield put(
      requestCallbackFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* fetchSubscriptionsSaga() {
  try {
    const response: AxiosResponse<{ data: ISubscription[] }> = yield call(getSubscriptions);

    yield put(
      fetchSubscriptionsSuccess({
        subscriptions: response.data,
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchSubscriptionsFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* updateSubscriptionAddressSaga(
  action: ReturnType<typeof updateSubscriptionAddressRequest>,
) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      updateSubscriptionAddress(action.payload.id, action.payload.params),
    );

    yield put(
      updateSubscriptionAddressSuccess({
        subscription: {
          id: action.payload.id,
          shippingAddress: response.data.shippingAddress || response.data,
        },
      }),
    );
    notifySuccess(
      translateOutsideReact('SUBSCRIPTION.TOAST.ADDRESS_UPDATED', 'Versandadresse aktualisiert'),
    );
  } catch (e: unknown) {
    yield put(
      updateSubscriptionAddressFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact('SUBSCRIPTION.TOAST.GENERIC_ERROR', 'Es ist ein Fehler aufgetreten.'),
    );
  }
}

function* cancelSubscriptionSaga(action: ReturnType<typeof cancelSubscriptionRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      cancelSubscription(action.payload.id, action.payload.params),
    );

    yield put(
      cancelSubscriptionSuccess({
        // TODO: remove after api ready
        subscription: { ...response?.data, id: action.payload.id },
      }),
    );
    notifySuccess(
      translateOutsideReact(
        'SUBSCRIPTION.TOAST.CANCELLED',
        'Dein Abo wurde erfolgreich gekündigt.',
      ),
    );
  } catch (e: unknown) {
    yield put(
      cancelSubscriptionFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.CANCEL_ERROR',
          'Dein Abo konnte nicht gekündigt werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* fetchCancellationFlowSaga(action: ReturnType<typeof fetchCancellationFlowRequest>) {
  try {
    const response: AxiosResponse<{ data: ISubscription }> = yield call(() =>
      fetchCancellationFlow(action.payload.id),
    );

    yield put(
      fetchCancellationFlowSuccess({
        subscription: response.data,
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchCancellationFlowFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* pauseSubscriptionSaga(action: ReturnType<typeof pauseSubscriptionRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      pauseSubscription(action.payload.id, action.payload.params),
    );

    yield put(
      pauseSubscriptionSuccess({
        subscription: response.data,
      }),
    );

    // Refresh subscription + order schedule so the current-order card and dates reflect
    // the new billing schedule without a manual reload.
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.id }));

    notifySuccess(
      translateOutsideReact('SUBSCRIPTION.TOAST.PAUSED', 'Dein Abo ist jetzt pausiert.'),
    );
  } catch (e: unknown) {
    yield put(
      pauseSubscriptionFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.PAUSE_ERROR',
          'Dein Abo konnte nicht pausiert werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* resumeSubscriptionSaga(action: ReturnType<typeof resumeSubscriptionRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      resumeSubscription(action.payload.id),
    );

    yield put(
      resumeSubscriptionSuccess({
        subscription: response.data,
      }),
    );

    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.id }));

    notifySuccess(
      translateOutsideReact('SUBSCRIPTION.TOAST.ACTIVE_AGAIN', 'Dein Abo ist wieder aktiv.'),
    );
  } catch (e: unknown) {
    yield put(
      resumeSubscriptionFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.RESUME_ERROR',
          'Dein Abo konnte nicht fortgesetzt werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* reactivateSubscriptionSaga(action: ReturnType<typeof reactivateSubscriptionRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      reactivateSubscription(action.payload.id),
    );

    yield put(
      reactivateSubscriptionSuccess({
        // TODO: remove after api ready
        subscription: { ...response?.data, id: action.payload.id },
      }),
    );
    notifySuccess(
      translateOutsideReact('SUBSCRIPTION.TOAST.ACTIVE_AGAIN', 'Dein Abo ist wieder aktiv.'),
    );
  } catch (e: unknown) {
    yield put(
      reactivateSubscriptionFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.REACTIVATE_ERROR',
          'Dein Abo konnte nicht reaktiviert werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* updateSubscriptionNextChargeDateSaga(
  action: ReturnType<typeof updateSubscriptionNextChargeDateRequest>,
) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      updateSubscriptionNextChargeDate(action.payload.id, action.payload.params),
    );

    yield put(
      updateSubscriptionNextChargeDateSuccess({
        subscription: response.data,
      }),
    );
  } catch (e: unknown) {
    yield put(
      updateSubscriptionNextChargeDateFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact('SUBSCRIPTION.TOAST.GENERIC_ERROR', 'Es ist ein Fehler aufgetreten.'),
    );
  }
}

function* rescheduleOrderSaga(action: ReturnType<typeof rescheduleOrderRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      rescheduleOrder(action.payload.id, action.payload.params),
    );

    yield put(
      rescheduleOrderSuccess({
        subscription: response.data,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.id }));
    notifySuccess(
      translateOutsideReact(
        'SUBSCRIPTION.TOAST.DATE_CHANGED',
        'Bestelldatum geändert. E-Mail folgt',
      ),
    );
  } catch (e: unknown) {
    yield put(
      rescheduleOrderFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.DATE_ERROR',
          'Das Bestelldatum konnte nicht ausgewählt werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* placeOrderSaga(action: ReturnType<typeof placeOrderRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() => placeOrder(action.payload.id));

    yield put(
      placeOrderSuccess({
        subscription: response.data,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.id }));
    notifySuccess(
      translateOutsideReact(
        'SUBSCRIPTION.TOAST.ORDER_CREATED',
        'Bestellung ausgelöst. E-Mail folgt',
      ),
    );
  } catch (e: unknown) {
    yield put(
      placeOrderFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.ORDER_CREATE_ERROR',
          'Deine Bestellung konnte nicht erstellt werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* skipNextOrderSaga(action: ReturnType<typeof skipNextOrderRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() => skipNextOrder(action.payload.id));

    yield put(
      skipNextOrderSuccess({
        subscription: response.data,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.id }));
    notifySuccess(
      translateOutsideReact(
        'SUBSCRIPTION.TOAST.ORDER_SKIPPED',
        'Bestellung übersprungen. E-Mail folgt',
      ),
    );
  } catch (e: unknown) {
    yield put(
      skipNextOrderFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.ORDER_SKIP_ERROR',
          'Deine Bestellung konnte nicht übersprungen werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* changePaymentMethodSaga(action: ReturnType<typeof changePaymentMethodRequest>) {
  try {
    const response: AxiosResponse<{ id: number; subscription: ISubscription }> = yield call(() =>
      changePaymentMethod(action.payload.id, action.payload.params),
    );

    yield put(
      changePaymentMethodSuccess({
        subscription: response.data,
      }),
    );
    notifySuccess(
      translateOutsideReact('SUBSCRIPTION.TOAST.PAYMENT_UPDATED', 'Zahlungsdaten aktualisiert'),
    );
  } catch (e: unknown) {
    yield put(
      changePaymentMethodFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact('SUBSCRIPTION.TOAST.GENERIC_ERROR', 'Es ist ein Fehler aufgetreten.'),
    );
  }
}

function* addDiscountSaga(action: ReturnType<typeof addDiscountRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      addDiscount(action.payload.id, action.payload.params),
    );

    yield put(
      addDiscountSuccess({
        subscription: response.data,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchCustomerRequest());
    notifySuccess(
      translateOutsideReact('SUBSCRIPTION.TOAST.DISCOUNT_APPLIED', 'Rabatt erfolgreich angewendet'),
    );
  } catch (e: unknown) {
    const serverError =
      (e as AxiosError<{ error?: string; message?: string }>)?.response?.data?.error ||
      (e as AxiosError<{ error?: string; message?: string }>)?.response?.data?.message;
    // Surface the API's actual message (e.g. Shopify userError / insufficient
    // credit) instead of the opaque "Request failed with status code 422".
    yield put(
      addDiscountFailure({
        error: serverError || (e instanceof Error ? e.message : 'Unknown error'),
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.DISCOUNT_ADD_ERROR',
          'Der Rabattcode konnte nicht hinzugefügt werden.',
        ),
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.CHECK_INPUT',
          'Bitte überprüfe deine Eingabe und versuche es erneut.',
        ),
      ),
    );
  }
}

function* removeDiscountSaga(action: ReturnType<typeof removeDiscountRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      removeDiscount(action.payload.id, action.payload.params),
    );

    yield put(
      removeDiscountSuccess({
        subscription: response.data,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchCustomerRequest());
    notifySuccess(translateOutsideReact('SUBSCRIPTION.TOAST.DISCOUNT_REMOVED', 'Rabatt entfernt'));
  } catch (e: unknown) {
    yield put(
      removeDiscountFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      toastMessage(
        translateOutsideReact(
          'SUBSCRIPTION.TOAST.DISCOUNT_REMOVE_ERROR',
          'Der Rabattcode konnte nicht entfernt werden.',
        ),
        translateOutsideReact('SUBSCRIPTION.TOAST.RETRY', 'Bitte versuche es erneut.'),
      ),
    );
  }
}

function* swapLineSaga(action: ReturnType<typeof swapLineRequest>): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      swapLine(action.payload.id, action.payload.params),
    );
    const subscriptionId = action.payload.id;

    yield put(
      swapLineSuccess({
        id: subscriptionId,
        lineItem: response.data,
        billingPolicy: {
          // Optimistic local echo, overwritten by the refetch below.
          interval: 'DAY',
          intervalCount: action.payload.params.billingPolicy.intervalCount,
        },
      }),
    );

    // Sync settings with the updated subscription options to hide the update banner
    const currentOptions: any = yield select(getSubscriptionOptionsSelector);
    yield put(updateSettings({ subscriptionOptions: currentOptions }));

    // Re-fetch subscriptions and order schedule to get updated prices
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.id }));

    notifySuccess(translateOutsideReact('SUBSCRIPTION.TOAST.UPDATED', 'Abo erfolgreich geändert'), {
      autoClose: 10000,
    });
  } catch (e: unknown) {
    yield put(
      swapLineFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact('SUBSCRIPTION.TOAST.GENERIC_ERROR', 'Es ist ein Fehler aufgetreten.'),
      { autoClose: 10000 },
    );
  }
}

function* fetchFlowRewardBannerSaga(action: ReturnType<typeof fetchFlowRewardBannerRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      fetchFlowRewardBanner(action.payload.subscriptionId),
    );

    yield put(
      fetchFlowRewardBannerSuccess({ id: action.payload.subscriptionId, data: response.data }),
    );
  } catch (e: unknown) {
    yield put(
      fetchFlowRewardBannerFailure({
        error: {
          type: SubscriptionError.FLOW_BANNER_NOT_FOUND,
          message: e instanceof Error ? e.message : 'Unknown error',
        },
      }),
    );
  }
}

function* fetchBenefitContentSaga(action: ReturnType<typeof fetchBenefitContentRequest>) {
  try {
    const response: AxiosResponse<{ data: ISubscription }> = yield call(() =>
      fetchBenefitContent(action.payload.id),
    );

    yield put(
      fetchBenefitContentSuccess({
        subscription: response.data,
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchBenefitContentFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* applyBenefitSaga(action: ReturnType<typeof applyBenefitRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      applyBenefit(action.payload.id, action.payload.params),
    );

    yield put(
      applyBenefitSuccess({
        subscription: { ...response?.data, id: action.payload.id },
      }),
    );
  } catch (e: unknown) {
    yield put(
      applyBenefitFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* fetchCancellationOfferSaga(action: ReturnType<typeof fetchCancellationOfferRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      fetchCancellationOffer(action.payload.subscriptionId, action.payload.cancellationFlowId),
    );

    yield put(
      fetchCancellationOfferSuccess({
        id: action.payload.subscriptionId,
        data: response.data,
        cancellationFlowId: action.payload.cancellationFlowId,
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchCancellationOfferFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* applyCancellationOfferSaga(action: ReturnType<typeof applyCancellationOfferRequest>) {
  try {
    const response: AxiosResponse<ISubscription> = yield call(() =>
      applyCancellationOffer(action.payload.id, action.payload.params),
    );

    yield put(
      applyCancellationOfferSuccess({
        id: action.payload.id,
        data: response.data,
      }),
    );
  } catch (e: unknown) {
    yield put(
      applyCancellationOfferFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* recalculateProductSaga(action: ReturnType<typeof recalculateProductRequest>) {
  try {
    const { pet: payload, currentPreferences } = action.payload;

    const calculatedKcal = calcScore(
      payload.weight,
      payload.activity,
      payload.body,
      payload.dog_stage === 'puppy' ? 'puppy' : 'adult',
      payload.adult_weight,
      payload.snack_level || 'none',
    );
    const finalKcal = calculatedKcal;
    const { disabledRecipes: allergyDisabledRecipes, reasons } = getDisabledRecipesFromAllergies(
      payload.allergies || [],
    );

    const rawData: any[] = yield call(fetchRecommendation, finalKcal.toString());
    const recommendation = rawData?.[0];
    if (!recommendation) {
      throw new Error('No recommendation received from API');
    }

    const response: RecalculateProductResponse = transformRecommendationToProduct(
      recommendation,
      allergyDisabledRecipes,
      reasons,
      currentPreferences,
    );

    const pet: IPet | null = yield select(getPetSelector);
    const disabledRecipes = pet ? getDisabledRecipes(pet) : [];
    const matchingVariant = response.product.variants[0];
    const selectedVariant = matchingVariant
      ? {
          variantTitle: matchingVariant.title || '',
          variantId: matchingVariant.id || '',
          sku: matchingVariant.sku || '',
          variantRecipes: matchingVariant.selectedOptions?.recipes || [],
          sachets:
            matchingVariant.metafields?.product_info?.sachets?.map((sachet: any) => ({
              quantity: sachet.quantity || 0,
              serve: sachet.serve || 0,
              type: sachet.type || '',
              weight: {
                value: sachet.weight?.value || 0,
                unit: sachet.weight?.unit || 'g',
              },
            })) || [],
          variantPension: matchingVariant.selectedOptions?.pension || '',
          variantCycle: matchingVariant.selectedOptions?.cycle || 0,
        }
      : undefined;

    const productUpdatedData: ProductUpdatedData = {
      productTitle: response.product.title,
      productId: response.product.id,
      calories: response.calculatedCalories,
      disabledRecipes,
      selectedVariant,
    };

    yield put(
      recalculateProductSuccess({
        data: response,
        productUpdated: productUpdatedData,
      }),
    );
  } catch (e: unknown) {
    yield put(
      recalculateProductFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact(
        'SUBSCRIPTION.TOAST.RECALCULATE_ERROR',
        'Produkt konnte nicht neu berechnet werden',
      ),
    );
  }
}

function* updatePetAndSwapLineSaga(
  action: ReturnType<typeof updatePetAndSwapLineRequest>,
): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      updatePetAndSwapLine(action.payload.id, action.payload.params),
    );

    const subscriptionId = action.payload.id;
    const lineItem: any = yield select(getLineItemSelector);
    const pet: any = yield select(getPetSelector);
    const petName = pet?.name || translateOutsideReact('GLOBAL.YOUR_DOG.ACC', 'deinen Hund');

    // Present only for corrective updates: the backend clears
    // `food_recommendation.update_required` and returns the refreshed values.
    const foodRecommendation = response.data?.foodRecommendation;

    yield put(
      updatePetAndSwapLineSuccess({
        id: subscriptionId,
        lineItem: response.data,
        billingPolicy: {
          // Optimistic local echo, overwritten by the refetch below.
          interval: 'DAY',
          intervalCount: action.payload.params.billingPolicy.intervalCount,
        },
        ...(foodRecommendation ? { foodRecommendation } : {}),
      }),
    );

    yield put(recalculateProductReset());

    yield put(
      fetchSubscriptionsRequest({
        message: translateOutsideReact(
          'SUBSCRIPTION.RECALCULATING_RECIPE',
          'Das neue Rezept für {name} wird gerade frisch berechnet ...',
          { name: petName },
        ),
      }),
    );

    yield fork(function* () {
      yield take(FETCH_SUBSCRIPTIONS_SUCCESS);

      // Sync settings with the updated subscription options to hide the update banner
      const currentOptions: any = yield select(getSubscriptionOptionsSelector);
      yield put(updateSettings({ subscriptionOptions: currentOptions }));

      const updatedLineItem: any = yield select(getLineItemSelector);
      const productShopifyId = response.data?.productShopifyId || updatedLineItem?.productShopifyId;
      if (productShopifyId) {
        yield put(
          fetchProductRequest({
            productId: productShopifyId,
          }),
        );
      }
    });

    notifySuccess(translateOutsideReact('SUBSCRIPTION.TOAST.UPDATED', 'Abo erfolgreich geändert'));
  } catch (e: unknown) {
    yield put(
      updatePetAndSwapLineFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact('SUBSCRIPTION.TOAST.GENERIC_ERROR', 'Es ist ein Fehler aufgetreten.'),
    );
  }
}

function* updatePetDetailsSaga(
  action: ReturnType<typeof updatePetDetailsRequest>,
): Generator<any, void, any> {
  try {
    const lineItem: any = yield select(getLineItemSelector);
    const lineId = lineItem?.id;
    if (!lineId) {
      throw new Error('Line item not found');
    }
    const response: AxiosResponse<{ success: boolean; data: IPet }> = yield call(() =>
      updatePetDetails(action.payload.id, action.payload.name, lineId),
    );

    if (response.data.success) {
      yield put(
        updatePetDetailsSuccess({
          pet: response.data.data,
        }),
      );
      notifySuccess(translateOutsideReact('SUBSCRIPTION.TOAST.NAME_UPDATED', 'Name aktualisiert'));

      // Re-fetch subscriptions to get the full updated data
      yield put(fetchSubscriptionsRequest());
    } else {
      throw new Error('Update failed');
    }
  } catch (e: unknown) {
    yield put(
      updatePetDetailsFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact(
        'SUBSCRIPTION.TOAST.NAME_ERROR',
        'Fehler beim Aktualisieren des Namens',
      ),
    );
  }
}

export function* subscriptionsSaga() {
  yield all([
    takeLatest(FETCH_SUBSCRIPTIONS_REQUEST, fetchSubscriptionsSaga),
    takeLatest(UPDATE_SUBSCRIPTION_ADDRESS_REQUEST, updateSubscriptionAddressSaga),
    takeLatest(CANCEL_SUBSCRIPTION_REQUEST, cancelSubscriptionSaga),
    takeLatest(FETCH_CANCELLATION_FLOW_REQUEST, fetchCancellationFlowSaga),
    takeLatest(PAUSE_SUBSCRIPTION_REQUEST, pauseSubscriptionSaga),
    takeLatest(RESUME_SUBSCRIPTION_REQUEST, resumeSubscriptionSaga),
    takeLatest(REACTIVATE_SUBSCRIPTION_REQUEST, reactivateSubscriptionSaga),
    takeLatest(UPDATE_SUBSCRIPTION_NEXT_CHARGE_DATE_REQUEST, updateSubscriptionNextChargeDateSaga),
    takeLatest(RESCHEDULE_ORDER_REQUEST, rescheduleOrderSaga),
    takeLatest(PLACE_ORDER_REQUEST, placeOrderSaga),
    takeLatest(SKIP_NEXT_ORDER_REQUEST, skipNextOrderSaga),
    takeLatest(CHANGE_PAYMENT_METHOD_REQUEST, changePaymentMethodSaga),
    takeLatest(ADD_DISCOUNT_REQUEST, addDiscountSaga),
    takeLatest(REMOVE_DISCOUNT_REQUEST, removeDiscountSaga),
    takeLatest(SWAP_LINE_REQUEST, swapLineSaga),
    takeLatest(FETCH_FLOW_REWARD_BANNER_REQUEST, fetchFlowRewardBannerSaga),
    takeLatest(FETCH_BENEFIT_CONTENT_REQUEST, fetchBenefitContentSaga),
    takeLatest(APPLY_BENEFIT_REQUEST, applyBenefitSaga),
    takeLatest(FETCH_CANCELLATION_OFFER_REQUEST, fetchCancellationOfferSaga),
    takeLatest(APPLY_CANCELLATION_OFFER_REQUEST, applyCancellationOfferSaga),
    takeLatest(REQUEST_CALLBACK_REQUEST, requestCallbackSaga),
    takeLatest(RECALCULATE_PRODUCT_REQUEST, recalculateProductSaga),
    takeLatest(UPDATE_PET_AND_SWAP_LINE_REQUEST, updatePetAndSwapLineSaga),
    takeLatest(FETCH_ALLERGIES_REQUEST, fetchAllergiesSaga),
    takeLatest(FETCH_HEALTH_ISSUES_REQUEST, fetchHealthIssuesSaga),
    takeLatest(UPDATE_PET_DETAILS_REQUEST, updatePetDetailsSaga),
  ]);
}
